import React, { useEffect, useRef, useState } from "react";
import bgImage from "../assets/hero-bg.png";
import { Link as RouterLink } from 'react-router-dom';
import { Link as ScrollLink, Events } from 'react-scroll';
import { useTypewriter, Cursor } from 'react-simple-typewriter';
import { allBlogPosts } from "../blogData";
// We no longer need Helmet!

function HomePage() {
  const carouselRef = useRef(null);
  const [isScrolling, setIsScrolling] =  useState(false);

  // This effect ensures the page starts at the top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Typewriter hook, now aware of scrolling
  const [text] = useTypewriter({
    words: [
      'Production-Ready Techpacks',
      'Flawless Samples',
      'Scalable Production'
    ],
    loop: isScrolling ? 1 : 0, // Pauses loop when scrolling
    typeSpeed: 35,
    deleteSpeed: 30,
  });

  // This effect pauses the typewriter
  useEffect(() => {
    const handleScrollBegin = () => setIsScrolling(true);
    const handleScrollEnd = () => setIsScrolling(false);

    Events.scrollEvent.register('begin', handleScrollBegin);
    Events.scrollEvent.register('end', handleScrollEnd);

    return () => {
      Events.scrollEvent.remove('begin');
      Events.scrollEvent.remove('end');
    };
  }, []);

  // This effect controls the JavaScript carousel
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    let intervalId = null;

    const startAutoScroll = () => {
      clearInterval(intervalId); // Clear any existing interval
      intervalId = setInterval(() => {
        if (carousel.scrollLeft >= carousel.scrollWidth / 2) {
          carousel.scrollLeft = 0;
        }
        carousel.scrollBy({ left: 1, behavior: 'auto' });
      }, 25);
    };

    const stopAutoScroll = () => {
      clearInterval(intervalId);
    };

    // Add mouse listeners
    carousel.addEventListener('mouseenter', stopAutoScroll);
    carousel.addEventListener('mouseleave', startAutoScroll);
    
    // Add page scroll listeners to pause carousel
    Events.scrollEvent.register('begin', stopAutoScroll);
    Events.scrollEvent.register('end', startAutoScroll);

    startAutoScroll(); // Start it
    
    return () => {
      // Full cleanup
      clearInterval(intervalId);
      carousel.removeEventListener('mouseenter', stopAutoScroll);
      carousel.removeEventListener('mouseleave', startAutoScroll);
      Events.scrollEvent.remove('begin');
      Events.scrollEvent.remove('end');
    };
  }, []); // Empty array ensures this runs only once

  return (
    <div className="w-full">
      {/* --- THIS IS THE NEW REACT 19 METHOD FOR SEO --- */}
      <title>Techpack Creators - Expert Fashion Design & Manufacturing</title>
      <meta 
        name="description" 
        content="Techpack Creators turns your fashion vision into reality. We offer expert techpack design, flat sketches, low-MOQ manufacturing support, and Clo3D services for global brands." 
      />
      {/* --- END OF NEW METHOD --- */}

      {/* Hero Section */}
      <div
        className="h-screen bg-cover bg-right flex items-center justify-center text-white relative"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20"></div>
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-4">
          <h1
            className="text-6xl md:text-8xl text-white mb-4 drop-shadow-md"
            style={{ fontFamily: "'Lobster', cursive" }}
          >
            Techpack Creators
          </h1>
          <h2 className="text-2xl md:text-4xl font-bold text-white mt-2 font-montserrat drop-shadow-lg flex flex-col md:flex-row items-center justify-center flex-wrap">
            <span className="md:mr-3">Translating Your Vision into</span>
            <span>
              <span className="text-blue-400 font-bold">{text}</span>
              <Cursor cursorStyle='|' />
            </span>
          </h2>
          <p className="text-base md:text-xl max-w-2xl mt-6 mb-10 font-montserrat drop-shadow">
            Crafting your signature collection.
          </p>
          <ScrollLink
            to="contact"
            spy={true}
            smooth={true}
            duration={800}
            className="px-6 py-3 md:px-8 text-base md:text-lg bg-transparent border-2 border-white text-white rounded-lg hover:bg-white hover:text-slate-800 transition duration-300 font-montserrat font-bold cursor-pointer"
          >
            Let's Create
          </ScrollLink>
        </div>
      </div>

      {/* Services Section */}
      <section className="min-h-screen flex items-center justify-center py-16 bg-gray-100">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-12 font-montserrat">
            Our Services
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-10 max-w-6xl w-full mx-auto">
            {[
              { img: "https://images.pexels.com/photos/4348401/pexels-photo-4348401.jpeg", title: "Techpack Design", desc: "Detailed techpacks to communicate your design ideas accurately to manufacturers." },
              { img: "https://images.pexels.com/photos/4614204/pexels-photo-4614204.jpeg", title: "Sourcing Assistance", desc: "Helping you find quality fabrics and trims at competitive prices." },
              { img: "https://images.pexels.com/photos/34563855/pexels-photo-34563855.jpeg", title: "Sample Development", desc: "Producing accurate samples for approvals before bulk production." },
              { img: "https://images.pexels.com/photos/12906322/pexels-photo-12906322.jpeg", title: "Production Support", desc: "Assistance and monitoring throughout your garment production process." }
            ].map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden transition-transform duration-300 hover:scale-105 hover:shadow-2xl text-left"
              >
                <img src={service.img} alt={service.title} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 font-montserrat text-gray-800">{service.title}</h3>
                  <p className="text-gray-700 font-montserrat">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* --- "See All" BUTTON --- */}
          <div className="mt-16">
            <a
              href="/services"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-yellow-800 text-white rounded-lg hover:bg-yellow-900 transition duration-300 font-montserrat text-lg shadow-lg"
            >
              See All Our Services
            </a>
          </div>

        </div>
      </section>

      {/* Portfolio Section */}
      <section className="min-h-screen bg-gradient-to-r from-pink-100 to-pink-200 flex flex-col items-center justify-center px-6 py-16">
        <h2 className="text-4xl font-bold mb-12 font-montserrat">Our Portfolio</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl w-full">
          {[
            { img: "https://images.pexels.com/photos/3280728/pexels-photo-3280728.png", title: "Streetwear Jacket" },
            { img: "https://images.pexels.com/photos/10460442/pexels-photo-10460442.jpeg", title: "Summer Collection" },
            { img: "https://images.pexels.com/photos/7959624/pexels-photo-7959624.jpeg", title: "Formal Suit Design" }
          ].map((item, index) => (
            <div key={index} className="relative group overflow-hidden rounded-2xl shadow-lg">
              <img src={item.img} alt={item.title} className="w-full h-80 object-cover transform group-hover:scale-110 transition duration-500" />
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                <h3 className="text-white text-xl font-bold">{item.title}</h3>
              </div>
            </div>
          ))}
        </div>

        {/* --- THIS IS THE NEW "See All" BUTTON --- */}
        <div className="mt-16">
          <a
            href="/portfolio"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-3 bg-yellow-800 text-white rounded-lg hover:bg-yellow-900 transition duration-300 font-montserrat text-lg shadow-lg"
          >
            See Our Full Portfolio
          </a>
        </div>
        {/* --- END OF NEW BUTTON --- */}

      </section>
      
      {/* Blog Section */}
      <section
        id="blog"
        className="min-h-screen flex flex-col justify-center bg-gray-50 text-black px-6 sm:px-10 lg:px-16 py-16"
      >
        <h2 className="text-4xl md:text-5xl font-montserrat mb-12 text-center">
          Featured Blog Posts
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl w-full mx-auto">
          {/* We only show the first 3 posts on the homepage */}
          {allBlogPosts.slice(0, 3).map((blog, idx) => ( 
            <a
              href={`/blog/${blog.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              key={idx}
              className="bg-white shadow-lg rounded-2xl overflow-hidden hover:scale-105 transform transition duration-500 ease-in-out flex flex-col"
            >
              <div className="w-full aspect-square">
                <img
                  src={blog.img}
                  alt={blog.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 sm:p-6 flex flex-col justify-between flex-1">
                <h3 className="text-lg sm:text-xl font-montserrat mb-2 sm:mb-3">
                  {blog.title}
                </h3>
                <p className="text-gray-600 font-montserrat text-sm sm:text-base">
                  {blog.description}
                </p>
              </div>
            </a>
          ))}
        </div>
      </section>
      
      {/* Clients Section */}
      <section className="min-h-screen bg-gradient-to-r from-yellow-50 to-yellow-200 flex flex-col items-center justify-center px-6 overflow-hidden">
        <h2 className="text-4xl font-bold mb-12 font-montserrat">What Our Clients Say</h2>
        
        <div ref={carouselRef} className="w-full max-w-6xl flex flex-nowrap space-x-6 overflow-x-auto p-4 hide-scrollbar">
          {[
            { img: "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg", text: "Amazing service! They brought my fashion ideas to life.", name: "Maxime(U.K)" },
            { img: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg", text: "Professional team with great communication.", name: "Joes(U.S.A)" },
            { img: "https://images.pexels.com/photos/712513/pexels-photo-712513.jpeg", text: "Delivered top-notch designs ahead of time.", name: "Lupe(U.S.A)" },
            { img: "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg", text: "Amazing service! They brought my fashion ideas to life.", name: "Maxime(U.K)" },
            { img: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg", text: "Professional team with great communication.", name: "Joes(U.S.A)" },
            { img: "https://images.pexels.com/photos/712513/pexels-photo-712513.jpeg", text: "Delivered top-notch designs ahead of time.", name: "Lupe(U.S.A)" }
          ].map((client, index) => (
            <div key={`first-${index}`} className="min-w-[300px] bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center">
              <img src={client.img} alt={client.name} className="rounded-full mb-4 w-24 h-24 object-cover border-4 border-yellow-200 shadow-md" />
              <p className="text-gray-600 italic text-center mb-4">“{client.text}”</p>
              <h3 className="font-bold">{client.name}</h3>
            </div>
          ))}
          {[
            { img: "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg", text: "Amazing service! They brought my fashion ideas to life.", name: "Maxime(U.K)" },
            { img: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg", text: "Professional team with great communication.", name: "Joes(U.S.A)" },
            { img: "https://images.pexels.com/photos/712513/pexels-photo-712513.jpeg", text: "Delivered top-notch designs ahead of time.", name: "Lupe(U.S.A)" },
            { img: "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg", text: "Amazing service! They brought my fashion ideas to life.", name: "Maxime(U.K)" },
            { img: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg", text: "Professional team with great communication.", name: "Joes(U.S.A)" },
            { img: "https://images.pexels.com/photos/712513/pexels-photo-712513.jpeg", text: "Delivered top-notch designs ahead of time.", name: "Lupe(U.S.A)" }
          ].map((client, index) => (
            <div key={`second-${index}`} className="min-w-[300px] bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center">
              <img src={client.img} alt={client.name} className="rounded-full mb-4 w-24 h-24 object-cover border-4 border-yellow-200 shadow-md" />
              <p className="text-gray-600 italic text-center mb-4">“{client.text}”</p>
              <h3 className="font-bold">{client.name}</h3>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="h-screen flex flex-col items-center justify-center px-6 bg-purple-100">
        <h2 className="text-3xl font-bold mb-6 font-montserrat">Get in Touch</h2>
        <form action="https://formspree.io/f/mblpnepy" method="POST" className="bg-white p-8 rounded-lg shadow-lg max-w-lg w-full">
          <input 
            type="text" 
            name="name" 
            placeholder="Your Name" 
            className="w-full p-3 mb-4 border rounded-lg font-montserrat" 
            required 
          />
          <input 
            type="email" 
            name="email" 
            placeholder="Your Email" 
            className="w-full p-3 mb-4 border rounded-lg font-montserrat" 
            required 
          />
          <input 
            type="tel" 
            name="phone" 
            placeholder="Your Contact/WhatsApp Number" 
            className="w-full p-3 mb-4 border rounded-lg font-montserrat" 
          />
          <textarea 
            name="message" 
            placeholder="Your Message" 
            className="w-full p-3 mb-4 border rounded-lg font-montserrat" 
            rows="4" 
          />
          <div className="text-center">
            <button type="submit" className="px-6 py-3 bg-yellow-800 text-white rounded-lg shadow-lg hover:bg-yellow-900 transition font-montserrat">
              Send Message
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default HomePage;